import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { boothQueueAPI } from '../../services/boothQueue';
import { uploadAudioToS3, uploadVideoToS3 } from '../../services/uploads';
import { FaVideo, FaCommentDots, FaSyncAlt, FaArrowLeft, FaSignOutAlt, FaBars, FaTimes, FaMicrophone, FaStop } from 'react-icons/fa';
import VideoCall from '../VideoCall/VideoCall';
import CallInviteModal from '../VideoCall/CallInviteModal';
import DeviceTestModal from './DeviceTestModal';
import EmployerPageTemplate from './EmployerPageTemplate';
import BoothAvailabilityInfo from './BoothAvailabilityInfo';
import TestQueueTag from './TestQueueTag';
import { useToast, ToastContainer } from '../common/Toast';
import { announceToScreenReader } from '../Accessibility/FocusManager';
import useDialogFocus from '../../hooks/useDialogFocus';
import './BoothQueueWaiting.css';
import AdminHeader from '../Layout/AdminHeader';
import { hydrateStreamMediaUrls, hydrateStreamMediaElements } from '../../utils/videoContentProcessor';
import { isBoothClosed } from '../../utils/availability';

const RECRUITER_END_BANNER_MESSAGE =
  'Recruiters are no longer available for live meetings. This booth is still open — you can leave a message for the recruiters.';

const BOOTH_CLOSED_BANNER_MESSAGE =
  'This booth is now closed. You can no longer join a meeting or leave a message. You can still view the employer page below.';

/** True when recruiter end time has passed and booth is still within its close window. */
function shouldShowRecruiterEndBanner(booth, now = new Date()) {
  if (!booth?.recruiterEndTime) return false;
  const recruiterEnd = new Date(booth.recruiterEndTime);
  if (Number.isNaN(recruiterEnd.getTime()) || now < recruiterEnd) return false;
  if (booth.closeTime) {
    const close = new Date(booth.closeTime);
    if (!Number.isNaN(close.getTime()) && now >= close) return false;
  }
  return true;
}

function getRecruiterUnavailableMessage(booth) {
  const custom = typeof booth?.recruiterUnavailableMessage === 'string'
    ? booth.recruiterUnavailableMessage.trim()
    : '';
  return custom || RECRUITER_END_BANNER_MESSAGE;
}

export default function BoothQueueWaiting() {
  const { eventSlug, boothId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { socket, connected: socketConnected } = useSocket();
  const { toasts, removeToast, showSuccess, showError, showInfo } = useToast();

  const [event, setEvent] = useState(null);
  const [booth, setBooth] = useState(null);
  // Persist numbers across refresh
  const initialQueuePos = (() => {
    const fromState = location.state?.queuePosition;
    const fromStorage = localStorage.getItem(`queuePos_${boothId}`);
    return typeof fromState === 'number' ? fromState : (fromStorage ? Number(fromStorage) : 0);
  })();
  const initialServing = (() => {
    const fromStorage = localStorage.getItem(`serving_${boothId}`);
    return fromStorage ? Number(fromStorage) : 0;
  })();
  const [queuePosition, setQueuePosition] = useState(initialQueuePos);
  const [currentServing, setCurrentServing] = useState(initialServing);
  const [waitingCount, setWaitingCount] = useState(0);
  const [peopleAhead, setPeopleAhead] = useState(0);
  const [queueToken, setQueueToken] = useState(() => {
    if (location.state?.queueToken) return location.state.queueToken;
    try {
      return localStorage.getItem(`queueToken_${boothId}`) || '';
    } catch (_e) {
      return '';
    }
  });
  const [queueEntryId, setQueueEntryId] = useState(null);
  // Joined through the booth's test link: the booth's close time does not lock this queue
  const [isTestQueue, setIsTestQueue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showLeaveMessageModal, setShowLeaveMessageModal] = useState(false);
  const [showTextMessagingModal, setShowTextMessagingModal] = useState(false);
  const [messageType, setMessageType] = useState('text');
  const [messageContent, setMessageContent] = useState('');
  const [textMessageContent, setTextMessageContent] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showMessagePreview, setShowMessagePreview] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingError, setRecordingError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1199);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const leaveRecordBtnRef = useRef(null);
  const leavePreviewRef = useRef(null);
  const leaveFocusTargetRef = useRef(null);
  const queuePanelRef = useRef(null);
  const queuePanelPreviousFocusRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Video call state
  const [callInvitation, setCallInvitation] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState(null);
  const [audioInputs, setAudioInputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [availabilityNow, setAvailabilityNow] = useState(() => new Date());

  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingVideoRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const announcementCounterRef = useRef(0);
  const hasLeftQueueRef = useRef(false); // Track if user has left the queue
  const isInCallRef = useRef(false); // Ref to avoid stale closure in interval callbacks
  const hasPendingInviteRef = useRef(false); // Skip restore when socket invite is already shown
  const callRestoreBlockedRef = useRef(false); // Stop retrying after a definitive 403
  const fallbackPromptedCallIdRef = useRef(null); // Don't re-prompt for a call the seeker already saw
  const recruiterEndBannerAnnouncedRef = useRef(''); // last announced message text (empty = not announced)
  const boothClosedAnnouncedRef = useRef(false);
  const queueEntryIdRef = useRef(null);
  const boothIdRef = useRef(boothId);
  const showTextMessagingModalRef = useRef(false);
  const recentIncomingMessageKeysRef = useRef(new Set());
  const userIdRef = useRef(user?._id ? String(user._id) : '');
  const handleNewMessageFromRecruiterRef = useRef(null);
  // Highest unread count already notified, so polling can catch messages whose
  // socket event never arrived without re-announcing ones we already showed.
  const lastSeenUnreadRef = useRef(0);

  // Keep ref in sync with state so interval callbacks always see the latest value
  useEffect(() => {
    isInCallRef.current = isInCall;
  }, [isInCall]);

  useEffect(() => {
    queueEntryIdRef.current = queueEntryId;
  }, [queueEntryId]);

  useEffect(() => {
    boothIdRef.current = boothId;
  }, [boothId]);

  useEffect(() => {
    showTextMessagingModalRef.current = showTextMessagingModal;
  }, [showTextMessagingModal]);

  useEffect(() => {
    userIdRef.current = user?._id ? String(user._id) : '';
  }, [user?._id]);

  useEffect(() => {
    hasPendingInviteRef.current = !!(showInviteModal || pendingInvitation || callInvitation);
  }, [showInviteModal, pendingInvitation, callInvitation]);

  const tryRestoreActiveCall = async (queueData) => {
    // Only recover after a page refresh mid-call. New invites are handled via socket modal.
    if (!queueData || queueData.status !== 'in_meeting' || isInCallRef.current) return;
    if (hasPendingInviteRef.current || callRestoreBlockedRef.current) return;
    if (sessionStorage.getItem(`inActiveCall_${boothId}`) !== '1') return;

    const queueEntryId = queueData.queueEntry?._id;
    if (!queueEntryId) return;

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const videoCallRes = await axios.get(`/api/video-call/by-queue/${queueEntryId}`, { headers });

      const callData = videoCallRes.data?.videoCall;
      if (videoCallRes.data?.success && callData) {
        // Mark as in-call immediately via ref to prevent duplicate recovery attempts
        isInCallRef.current = true;
        try {
          sessionStorage.setItem(`inActiveCall_${boothId}`, '1');
        } catch (_e) { /* ignore */ }
        setCallInvitation({
          id: callData._id || callData.id,
          roomName: callData.roomName,
          accessToken: callData.jobSeekerToken || callData.accessToken,
          userRole: 'jobseeker',
          booth: queueData.queueEntry.booth,
          event: queueData.queueEntry.event,
          participants: {
            recruiter: callData.recruiter,
            jobSeeker: user
          },
          metadata: {
            interpreterRequested: !!queueData.queueEntry.interpreterCategory,
            interpreterCategory: queueData.queueEntry.interpreterCategory,
            isTestCall: !!(callData.metadata?.isTestCall || queueData.queueEntry.isTest)
          }
        });
        setIsInCall(true);
      }
    } catch (callErr) {
      const status = callErr.response?.status;
      if (status === 404) return;
      if (status === 403) {
        callRestoreBlockedRef.current = true;
        console.warn('Unable to restore video call: unauthorized for this queue entry');
        return;
      }
      console.error('Error restoring video call from queue status:', callErr);
    }
  };


  useEffect(() => {
    // Reset queueToken when boothId changes to prevent using token from wrong booth
    if (location.state?.queueToken) {
      setQueueToken(location.state.queueToken);
    } else {
      try {
        setQueueToken(localStorage.getItem(`queueToken_${boothId}`) || '');
      } catch (_e) {
        setQueueToken('');
      }
    }
    setQueueEntryId(null);
    
    loadData();
    joinSocketRoom();

    // Do not leave the DB queue on beforeunload/refresh. sendBeacon cannot send
    // the auth header, and a successful leave here would drop people who only
    // refreshed. Tab close is handled by the socket disconnect grace period;
    // Exit / Return to event call leaveQueue explicitly.
    return () => {
      if (socket) {
        socket.emit('leave-booth-queue', { boothId, userId: user?._id ? String(user._id) : undefined });
      }
    };
  }, [eventSlug, boothId]);

  // Initialize speech synthesis on mount
  useEffect(() => {
    // Trigger voice loading immediately
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
    } else {
      console.warn('⚠️ Speech synthesis not supported in this browser');
    }
  }, []);

  useEffect(() => {
    if (!booth?.name) return;
    announceToScreenReader(`${booth.name} waiting area`);
  }, [booth?.name]);

  // Rewrite any legacy stream URLs on mounted video/audio elements
  useEffect(() => {
    hydrateStreamMediaElements(document.querySelector('.waiting-employer-pane, .content-grid-expanded'));
  }, [booth]);

  // Ensure we (re)join socket rooms after the socket connects/reconnects
  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      // Re-join booth room so recruiter sees updates in real-time
      joinSocketRoom();
    };

    socket.on('connect', handleConnect);
    if (socket.connected || socketConnected) {
      joinSocketRoom();
    }
    return () => {
      socket.off('connect', handleConnect);
    };
  }, [socket, socketConnected, boothId, user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket || !socketConnected) return;

    const onNewMessageFromRecruiter = (data) => {
      handleNewMessageFromRecruiterRef.current?.(data);
    };

    socket.on('queue-position-updated', handleQueueUpdate);
    socket.on('queue-serving-updated', handleServingUpdate);
    socket.on('queue-invited-to-meeting', handleMeetingInvite);
    socket.on('call_invitation', handleCallInvitation);
    socket.on('new-message-from-recruiter', onNewMessageFromRecruiter);
    // Detect server-side queue leaves (e.g., when recruiter ends call)
    socket.on('queue-updated', handleQueueUpdated);
    socket.on('queue_left', handleQueueUpdated);

    return () => {
      socket.off('queue-position-updated', handleQueueUpdate);
      socket.off('queue-serving-updated', handleServingUpdate);
      socket.off('queue-invited-to-meeting', handleMeetingInvite);
      socket.off('call_invitation', handleCallInvitation);
      socket.off('new-message-from-recruiter', onNewMessageFromRecruiter);
      socket.off('queue-updated', handleQueueUpdated);
      socket.off('queue_left', handleQueueUpdated);
    };
  // socketConnected is a React state from SocketContext (reliable), unlike socket?.connected (mutable property)
  }, [socket, socketConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat to keep connection alive and detect if user is still active
  useEffect(() => {
    const heartbeatInterval = setInterval(() => {
      if (socket && socket.connected && user?._id) {
        socket.emit('queue-heartbeat', { boothId, userId: String(user._id) });
      }
    }, 120000); // Send heartbeat every 2 minutes (less aggressive)

    return () => clearInterval(heartbeatInterval);
  }, [socket, boothId, user?._id]);

  // Periodic refresh of queue status to keep numbers updated
  useEffect(() => {
    if (!boothId || loading) return;
    
    const refreshInterval = setInterval(async () => {
      // Don't poll while in a call, leaving, uploading, or after the user has left the queue
      if (hasLeftQueueRef.current || isInCallRef.current || showLeaveMessageModal || isUploading) {
        if (hasLeftQueueRef.current) clearInterval(refreshInterval);
        return;
      }
      
      try {
        const queueData = await boothQueueAPI.getQueueStatus(boothId);
        if (queueData?.success) {
          setQueuePosition(queueData.position);
          setCurrentServing(queueData.currentServing);
          setWaitingCount(queueData.waitingCount ?? 0);
          setPeopleAhead(queueData.peopleAhead ?? 0);
          setQueueToken(queueData.token);
          setQueueEntryId(queueData.queueEntry?._id);
          setIsTestQueue(!!queueData.queueEntry?.isTest);
          syncUnreadFromStatus(queueData);

          // Fallback for a missed 'call_invitation' socket event.
          //
          // The invite reaches the job seeker only as an emit to their `user:<id>` room. If that
          // emit is lost the seeker keeps waiting while the recruiter sits alone in the room —
          // there is no Socket.IO Redis adapter, so it does not cross server instances, and a
          // socket that is down or reconnecting misses it the same way.
          //
          // POST /api/video-call/create sets the entry to 'invited' and then immediately to
          // 'in_meeting' before emitting, so 'in_meeting' is the state a stranded seeker is
          // actually left in — 'invited' only exists for the moment between those two writes.
          // tryRestoreActiveCall covers 'in_meeting' but only after the seeker has already
          // accepted a call on this device (it requires the inActiveCall_ session flag), so it
          // does not help a fresh invite. Show the same modal the socket event would have
          // shown; never auto-join, because the seeker has not consented to the call yet.
          if ((queueData.status === 'invited' || queueData.status === 'in_meeting')
            && queueData.queueEntry?._id
            && !isInCallRef.current
            && !hasPendingInviteRef.current
            && !hasLeftQueueRef.current) {
            try {
              const authToken = localStorage.getItem('token');
              const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
              const callRes = await axios.get(
                `/api/video-call/by-queue/${queueData.queueEntry._id}`, { headers }
              );
              const call = callRes.data?.videoCall;
              const callId = call && String(call._id || call.id);
              // Re-check the guards: the await gives the socket event time to land first, and
              // only prompt once per call so a declined invite is not re-offered every 5s.
              if (call
                && !hasPendingInviteRef.current
                && !isInCallRef.current
                && fallbackPromptedCallIdRef.current !== callId) {
                fallbackPromptedCallIdRef.current = callId;
                handleCallInvitation({
                  callId: call._id || call.id,
                  roomName: call.roomName,
                  recruiter: call.recruiter,
                  booth: call.booth || queueData.queueEntry.booth,
                  event: call.event || queueData.queueEntry.event,
                  accessToken: call.jobSeekerToken,
                  isTestCall: !!(call.metadata?.isTestCall || queueData.queueEntry.isTest)
                });
                return;
              }
            } catch (_callErr) {
              // Leave it for the next tick rather than disturbing the waiting page.
            }
          }

          try {
            localStorage.setItem(`queuePos_${boothId}`, String(queueData.position));
            localStorage.setItem(`serving_${boothId}`, String(queueData.currentServing));
            if (queueData.token) {
              localStorage.setItem(`queueToken_${boothId}`, queueData.token);
            }
          } catch (e) { }
        } else if (queueData?.shouldRedirect && !hasLeftQueueRef.current && !isInCallRef.current) {
          // Soft not-in-queue response (HTTP 200) — stop polling and leave waiting page
          hasLeftQueueRef.current = true;
          clearInterval(refreshInterval);
          console.log('User not in queue during periodic refresh, redirecting');
          navigate(`/booth-queue/${eventSlug}/${boothId}/entry`, { replace: true });
        }
      } catch (e) {
        // Legacy 404 handling — if user is truly not in queue, redirect them
        const is404 = e.response?.status === 404;
        const shouldRedirect = e.response?.data?.shouldRedirect;
        
        if (is404 && shouldRedirect && !hasLeftQueueRef.current && !isInCallRef.current) {
          hasLeftQueueRef.current = true;
          clearInterval(refreshInterval);
          // User is truly not in queue - redirect to queue entry page
          console.log('User not in queue during periodic refresh, redirecting');
          navigate(`/booth-queue/${eventSlug}/${boothId}/entry`, { replace: true });
          return; // Exit early
        }
        // For other errors, silently fail - don't break the UI
      }
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(refreshInterval);
  }, [boothId, loading, eventSlug, navigate, showLeaveMessageModal, isUploading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);

      // Load event first so we can request booth with assigned-recruiter count for this event
      const eventRes = await axios.get(`/api/events/slug/${eventSlug}`);
      const eventData = eventRes.data;

      let extractedEvent = null;
      if (eventData?.event) extractedEvent = eventData.event;
      else if (eventData?.success && eventData?.data) extractedEvent = eventData.data;
      else if (eventData?.name) extractedEvent = eventData;

      const eventId = extractedEvent?._id || extractedEvent?.id;
      const boothRes = await axios.get(`/api/booths/${boothId}`, {
        params: eventId ? { eventId: String(eventId) } : undefined
      });
      const boothData = boothRes.data;

      let extractedBooth = null;
      if (boothData?.booth) extractedBooth = boothData.booth;
      else if (boothData?.success && boothData?.data) extractedBooth = boothData.data;
      else if (boothData?.name) extractedBooth = boothData;

      const assignedCount = boothData?.assignedRecruitersCount ?? extractedBooth?.assignedRecruitersCount;
      if (typeof assignedCount === 'number' && extractedBooth) {
        extractedBooth = {
          ...extractedBooth,
          assignedRecruitersCount: assignedCount
        };
      }

      if (extractedEvent) setEvent(extractedEvent);
      if (extractedBooth) setBooth(extractedBooth);

      // Load queue status separately so a not-in-queue response doesn't break the page on refresh
      try {
        const queueData = await boothQueueAPI.getQueueStatus(boothId);
        if (queueData?.success) {
          setQueuePosition(queueData.position);
          setCurrentServing(queueData.currentServing);
          setWaitingCount(queueData.waitingCount ?? 0);
          setPeopleAhead(queueData.peopleAhead ?? 0);
          setQueueToken(queueData.token);
          setQueueEntryId(queueData.queueEntry?._id);
          setIsTestQueue(!!queueData.queueEntry?.isTest);
          // Seed the baseline on first load so existing unread messages are shown
          // on the badge without replaying a toast for each of them.
          lastSeenUnreadRef.current = queueData.unreadMessages || 0;
          setUnreadCount(queueData.unreadMessages || 0);
          await tryRestoreActiveCall(queueData);
          
          try {
            localStorage.setItem(`queuePos_${boothId}`, String(queueData.position));
            localStorage.setItem(`serving_${boothId}`, String(queueData.currentServing));
            if (queueData.token) {
              localStorage.setItem(`queueToken_${boothId}`, queueData.token);
            }
          } catch (e) { }
        } else if (queueData?.shouldRedirect) {
          // Soft not-in-queue (HTTP 200) — redirect off the waiting page
          console.log('User not in queue, redirecting to event page');
          navigate(`/events/registered/${eventSlug}`, { replace: true });
          return;
        } else {
          // Not in queue or backend returned a non-successful shape
          // Reset queueToken to prevent using token from wrong booth
          setQueueToken('');
          setQueueEntryId(null);
          try { localStorage.removeItem(`queueToken_${boothId}`); } catch (_e) { }
          // Do not reset to 0; retain last known value in UI for position
        }
      } catch (qErr) {
        // Legacy 404 Not Found (user not in queue after refresh)
        const is404 = qErr.response?.status === 404;
        const shouldRedirect = qErr.response?.data?.shouldRedirect;
        
        if (is404 && shouldRedirect) {
          // User is truly not in queue - redirect to event page
          // This happens if entry was legitimately removed or never existed
          console.log('User not in queue, redirecting to event page');
          navigate(`/events/registered/${eventSlug}`, { replace: true });
          return; // Exit early to prevent further execution
        }
        
        // For other errors or if backend says not to redirect, just reset state
        // Reset queueToken to prevent using token from wrong booth
        setQueueToken('');
        setQueueEntryId(null);
        try { localStorage.removeItem(`queueToken_${boothId}`); } catch (_e) { }
        // Keep existing values for position to avoid flashing zeros
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load queue information');
    } finally {
      setLoading(false);
    }
  };

  const joinSocketRoom = () => {
    if (socket && boothId && user?._id) {
      socket.emit('join-booth-queue', {
        boothId,
        userId: String(user._id),
        eventSlug
      });
    }
  };

  // Refresh queue status to get updated waitingCount
  const refreshQueueStatus = async () => {
    // Don't make status requests if user has left the queue or is in a call
    if (hasLeftQueueRef.current || isInCallRef.current) {
      return;
    }
    
    try {
      const queueData = await boothQueueAPI.getQueueStatus(boothId);
      if (queueData?.success) {
        setQueuePosition(queueData.position);
        setCurrentServing(queueData.currentServing);
        setWaitingCount(queueData.waitingCount ?? 0);
        setQueueToken(queueData.token);
        setQueueEntryId(queueData.queueEntry?._id);
        setIsTestQueue(!!queueData.queueEntry?.isTest);
        syncUnreadFromStatus(queueData);
        try {
          localStorage.setItem(`queuePos_${boothId}`, String(queueData.position));
          localStorage.setItem(`serving_${boothId}`, String(queueData.currentServing));
          if (queueData.token) {
            localStorage.setItem(`queueToken_${boothId}`, queueData.token);
          }
        } catch (e) { }
      }
    } catch (e) {
      // Silently fail - don't break the UI (not-in-queue is expected after leave)
    }
  };

  const handleQueueUpdate = (data) => {
    if (data?.queueEntry?.jobSeeker?._id === user?._id) {
      setQueuePosition(data.queueEntry.position);
      setQueueEntryId(data.queueEntry._id);
      try {
        localStorage.setItem(`queuePos_${boothId}`, String(data.queueEntry.position));
      } catch (e) { }
      speak('Your place in the queue has been updated.', 'polite');
      // Refresh queue status to update waitingCount
      refreshQueueStatus();
    } else {
      // Someone else joined/left - refresh to update waitingCount
      refreshQueueStatus();
    }
  };

  const handleServingUpdate = (data) => {
    if (data.boothId === boothId) {
      const oldServing = currentServing;
      setCurrentServing(data.currentServing);
      try { localStorage.setItem(`serving_${boothId}`, String(data.currentServing)); } catch (e) { }

      // Refresh queue status to update waitingCount
      refreshQueueStatus();

      // Announce serving number changes to screen readers
      if (oldServing !== data.currentServing) {
        const announcement = `Now serving number ${data.currentServing}`;
        announceToScreenReader(announcement);

        // Check if user is next or being called
        if (queuePosition === data.currentServing) {
          const urgentAnnouncement = 'It is now your turn! Please prepare for your meeting invitation.';
          announceToScreenReader(urgentAnnouncement);
          speak(urgentAnnouncement);
        } else if (queuePosition === data.currentServing + 1) {
          const nextAnnouncement = 'You are next in line. Please be ready.';
          announceToScreenReader(nextAnnouncement);
        }
      }
    }
  };

  const handleMeetingInvite = (data) => {
    if (data.userId === user._id && data.boothId === boothId) {
      // Navigate to meeting room
      navigate(`/meeting/${data.meetingId}`, {
        state: { fromQueue: true, eventSlug, boothId }
      });
    }
  };

  // Handle queue updates from server indicating the user has left or been removed
  const handleQueueUpdated = (data) => {
    try {
      // We get this on booth rooms; ensure it's this booth and it's our queue entry
      if (!data) return;
      const sameBooth = String(data.boothId) === String(boothId);
      // Check for leave actions: user left, left with message, or recruiter removal
      const isLeftOrRemoved = data.action === 'left'
        || data.action === 'left_with_message'
        || data.action === 'removed'
        || data.type === 'left';
      const je = data.queueEntry?.jobSeeker;
      const jobSeekerId = typeof je === 'string' ? je : je?._id;
      const isCurrentUser = String(jobSeekerId) === String(user._id);
      // The same job seeker can have an older, already-closed entry at this booth (e.g. they
      // left a meeting and rejoined the queue). A late update for that old entry must not
      // remove them from the entry they are waiting on now.
      const updatedEntryId = data.queueEntry?._id;
      const currentEntryId = queueEntryIdRef.current;
      const isOtherEntry = !!updatedEntryId && !!currentEntryId
        && String(updatedEntryId) !== String(currentEntryId);

      if (sameBooth && isLeftOrRemoved && isCurrentUser && !isOtherEntry) {
        // User has been removed from the queue (by recruiter or call ended)
        hasLeftQueueRef.current = true;
        setIsInCall(false);
        setCallInvitation(null);
        // Show a message if removed by recruiter
        if (data.action === 'removed') {
          showInfo('You have been removed from the queue by the recruiter');
        }
        // Redirect out of the waiting page
        navigate(`/events/registered/${eventSlug}`);
      } else if (sameBooth) {
        // Queue changed (someone joined/left) - refresh to update counts
        refreshQueueStatus();
      }
    } catch (err) {
      console.warn('Error handling queue-updated event:', err);
    }
  };

  // Play a short beep using Web Audio
  const playInviteSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 880; // A5
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      o.start();
      o.stop(ctx.currentTime + 0.6);
    } catch (e) {
      // ignore audio failures
    }
  };

  const enumerateDevicesAndShowModal = async (inviteData) => {
    try {
      // Request permissions so device labels are available
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    } catch (e) {
      // If user blocks, still attempt to enumerate
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audios = devices.filter(d => d.kind === 'audioinput');
      const videos = devices.filter(d => d.kind === 'videoinput');
      setAudioInputs(audios);
      setVideoInputs(videos);
      const savedAudio = localStorage.getItem('preferredAudioDeviceId');
      const savedVideo = localStorage.getItem('preferredVideoDeviceId');
      setSelectedAudioId(savedAudio || audios[0]?.deviceId || '');
      setSelectedVideoId(savedVideo || videos[0]?.deviceId || '');
      setShowInviteModal(true);
    } catch (err) {
      console.error('Failed to enumerate devices:', err);
      // Fallback: proceed without modal — pass inviteData directly because React
      // state (pendingInvitation) may not have updated yet in this closure.
      handleAcceptInviteWithData(inviteData);
    }
  };

  // Announce to screen readers using ARIA live regions
  const announceToScreenReader = (message) => {
    announcementCounterRef.current += 1;
    const uniqueId = `announcement-${Date.now()}-${announcementCounterRef.current}-${Math.random().toString(36).substr(2, 9)}`;
    // Keep only the latest announcement. The live region is aria-atomic="true",
    // so if multiple announcements coexist it re-reads ALL of them together (e.g.
    // a lingering "You said: ..." would be read again in front of an incoming
    // recruiter message). Replacing instead of stacking ensures the screen reader
    // announces only the newest message.
    setAnnouncements([{ id: uniqueId, message }]);
  };

  // A test entry (booth test link) is never locked by the booth's close time, and the
  // "recruiters are finishing up" banner doesn't apply to it either.
  const boothClosed = !isTestQueue && isBoothClosed(booth, availabilityNow);
  const showRecruiterEndBanner = !isTestQueue && !boothClosed && shouldShowRecruiterEndBanner(booth, availabilityNow);
  const recruiterUnavailableMessage = getRecruiterUnavailableMessage(booth);

  // Re-evaluate recruiter-end window and refresh booth fields (e.g. custom message)
  // so admin edits appear without a full page refresh.
  useEffect(() => {
    if (!boothId) return undefined;
    const eventId = event?._id || event?.id;
    // Wait until the current event is known so assignedRecruitersCount is scoped correctly.
    // Refreshing without eventId returns no count and can wipe a correct value from loadData.
    if (!eventId) return undefined;

    let cancelled = false;

    const extractBooth = (boothData) => {
      if (boothData?.booth) return boothData.booth;
      if (boothData?.success && boothData?.data) return boothData.data;
      if (boothData?.name) return boothData;
      return null;
    };

    const refreshBoothAvailability = async () => {
      setAvailabilityNow(new Date());
      try {
        const boothRes = await axios.get(`/api/booths/${boothId}`, {
          params: { eventId: String(eventId) }
        });
        let extractedBooth = extractBooth(boothRes.data);
        const count = boothRes.data?.assignedRecruitersCount ?? extractedBooth?.assignedRecruitersCount;
        if (typeof count === 'number' && extractedBooth) {
          extractedBooth = {
            ...extractedBooth,
            assignedRecruitersCount: count
          };
        }
        if (!cancelled && extractedBooth) setBooth(extractedBooth);
      } catch (_e) {
        // Keep existing booth state if refresh fails
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshBoothAvailability();
      }
    };

    // Refresh immediately once eventId is available, then on an interval.
    refreshBoothAvailability();
    const intervalId = setInterval(refreshBoothAvailability, 15000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [boothId, event?._id, event?.id]);

  // Announce when the recruiter-end banner appears, and again if the message text changes.
  useEffect(() => {
    if (!showRecruiterEndBanner) {
      recruiterEndBannerAnnouncedRef.current = '';
      return;
    }
    if (recruiterEndBannerAnnouncedRef.current === recruiterUnavailableMessage) {
      return;
    }
    recruiterEndBannerAnnouncedRef.current = recruiterUnavailableMessage;
    announceToScreenReader(recruiterUnavailableMessage);
  }, [showRecruiterEndBanner, recruiterUnavailableMessage]);

  // Once the booth closes, dismiss any open messaging dialog so the job seeker
  // can't submit into a booth the server will now reject.
  useEffect(() => {
    if (!boothClosed) {
      boothClosedAnnouncedRef.current = false;
      return;
    }
    setShowLeaveMessageModal(false);
    setShowTextMessagingModal(false);
    setShowMessageModal(false);
    setShowMessagePreview(false);
    if (!boothClosedAnnouncedRef.current) {
      boothClosedAnnouncedRef.current = true;
      announceToScreenReader(BOOTH_CLOSED_BANNER_MESSAGE);
    }
  }, [boothClosed]);

  // Simple, reliable speech function using native SpeechSynthesis
  const speak = (text) => {
    try {
      if (!('speechSynthesis' in window)) {
        console.warn('Speech synthesis not supported');
        return;
      }

      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      // Create utterance
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      utterance.lang = 'en-US';

      // Get voices and set English voice if available
      const voices = window.speechSynthesis.getVoices();
      const englishVoice = voices.find(voice => voice.lang.startsWith('en'));
      if (englishVoice) {
        utterance.voice = englishVoice;
      }

      // Event handlers
      // Speech event handlers (no console logs needed for normal operation)
      utterance.onstart = () => {
        // Speech started
      };

      utterance.onend = () => {
        // Speech completed
      };

      utterance.onerror = (event) => {
        // Only log non-interrupted errors (interrupted is expected when speech is cancelled)
        if (event.error !== 'interrupted') {
          console.error('❌ Speech error:', event.error);
        }
      };

      // Speak immediately
      window.speechSynthesis.speak(utterance);

    } catch (e) {
      console.error('Speech error:', e);
    }
  };


  const handleCallInvitation = (data) => {
    hasPendingInviteRef.current = true;
    console.log('Received call invitation:', data);
    setPendingInvitation(data);
    playInviteSound();
    const boothName = booth?.name || data?.booth?.name || '';
    const eventName = event?.name || data?.event?.name || '';
    const message = `You are invited to join a video call${boothName ? ` at ${boothName}` : ''}${eventName ? ` for ${eventName}` : ''}.`;
    speak(message);
    // Pass data directly so the error-fallback path can accept the invite even
    // before React processes the setPendingInvitation state update.
    enumerateDevicesAndShowModal(data);
  };

  // Core logic: accepts an invite given explicit data (avoids stale state closure issues)
  const handleAcceptInviteWithData = (data) => {
    if (!data) return;
    if (selectedAudioId) localStorage.setItem('preferredAudioDeviceId', selectedAudioId);
    if (selectedVideoId) localStorage.setItem('preferredVideoDeviceId', selectedVideoId);
    isInCallRef.current = true;
    try {
      sessionStorage.setItem(`inActiveCall_${boothId}`, '1');
    } catch (_e) { /* ignore */ }
    setCallInvitation({
      id: data.callId,
      roomName: data.roomName,
      accessToken: data.accessToken,
      userRole: 'jobseeker',
      booth: data.booth,
      event: data.event,
      participants: {
        recruiter: data.recruiter,
        jobSeeker: user
      },
      metadata: {
        interpreterRequested: false,
        interpreterCategory: null,
        isTestCall: !!data.isTestCall
      }
    });
    setShowInviteModal(false);
    setIsInCall(true);
  };

  const handleAcceptInvite = () => {
    // pendingInvitation is set via setPendingInvitation before the modal is shown,
    // so by the time the user clicks Accept it is always populated.
    handleAcceptInviteWithData(pendingInvitation);
  };

  const handleDeclineInvite = () => {
    const declinedCallId = pendingInvitation?.callId || pendingInvitation?.id;
    if (socket && declinedCallId) {
      socket.emit('call-invitation-declined', { callId: declinedCallId });
    }
    setShowInviteModal(false);
    setPendingInvitation(null);
    showInfo('Call invitation declined. You remain in the queue.');
    refreshQueueStatus();
  };

  const handleCallEnd = async (endData = {}) => {
    isInCallRef.current = false;
    try {
      sessionStorage.removeItem(`inActiveCall_${boothId}`);
    } catch (_e) { /* ignore */ }
    setCallInvitation(null);
    setIsInCall(false);

    // Recruiter refresh/disconnect should return job seeker to waiting queue view.
    if (endData?.reason === 'recruiter_disconnected') {
      showInfo('Recruiter disconnected. You were returned to the waiting queue.');
      await refreshQueueStatus();
      return;
    }

    // For ended/removed outcomes, navigate back to event details.
    navigate(`/events/registered/${eventSlug}`);
  };

  // Two-note "ting" chime for incoming recruiter messages.
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // A context created before any user gesture starts suspended and plays nothing.
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => { /* ignore */ });
      }

      const playNote = (frequency, startOffset, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;

        const startAt = audioContext.currentTime + startOffset;
        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.exponentialRampToValueAtTime(0.25, startAt + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

        oscillator.start(startAt);
        oscillator.stop(startAt + duration);
      };

      playNote(1175, 0, 0.18);
      playNote(1568, 0.1, 0.35);
    } catch (err) {
      console.log('Could not play notification sound:', err);
    }
  };

  // Sound + toast + screen reader announcement for a newly received recruiter message.
  const notifyIncomingMessage = ({ isBroadcast = false, content = '' } = {}) => {
    playNotificationSound();

    const label = isBroadcast ? 'Announcement from recruiter' : 'New message from recruiter';
    const text = typeof content === 'string' ? content.trim() : '';
    const preview = text.length > 120 ? `${text.slice(0, 117)}...` : text;

    showInfo(preview ? `${label}: ${preview}` : label, 0);
    announceToScreenReader(text ? `${label}: ${text}` : label);
  };

  // Drive the unread badge from the polled queue status. Socket events can be
  // missed entirely (e.g. the socket never joined the booth/user rooms), so the
  // count returned by the status endpoint is the reliable trigger.
  const syncUnreadFromStatus = (queueData) => {
    const unread = queueData?.unreadMessages || 0;

    // While the dialog is open the messages are already on screen, so only track
    // the count and leave the badge alone.
    if (showTextMessagingModalRef.current) {
      lastSeenUnreadRef.current = unread;
      return;
    }

    if (unread > lastSeenUnreadRef.current) {
      const latestUnread = (queueData?.queueEntry?.messages || [])
        .filter(msg => msg.sender === 'recruiter' && !msg.isRead)
        .pop();
      notifyIncomingMessage({
        isBroadcast: !!latestUnread?.isBroadcast,
        content: latestUnread?.type === 'text' ? latestUnread.content : ''
      });
    }

    lastSeenUnreadRef.current = unread;
    setUnreadCount(unread);
  };

  const handleNewMessageFromRecruiter = (data) => {
    const currentQueueEntryId = queueEntryIdRef.current;
    const currentBoothId = boothIdRef.current;
    const currentUserId = userIdRef.current;
    const incomingQueueId = data?.queueId != null ? String(data.queueId) : '';
    const incomingBoothId = data?.boothId != null ? String(data.boothId) : '';
    const incomingJobSeekerId = data?.jobSeekerId != null ? String(data.jobSeekerId) : '';
    const matchesQueue =
      currentQueueEntryId && incomingQueueId && incomingQueueId === String(currentQueueEntryId);
    const matchesBoothAndUser =
      currentBoothId &&
      incomingBoothId &&
      incomingBoothId === String(currentBoothId) &&
      currentUserId &&
      incomingJobSeekerId &&
      incomingJobSeekerId === currentUserId;

    if ((!matchesQueue && !matchesBoothAndUser) || !data?.message) {
      return;
    }

    const incomingCreatedAt = data.message.createdAt instanceof Date
      ? data.message.createdAt.getTime()
      : new Date(data.message.createdAt).getTime();
    const dedupeKey = data.message._id
      ? `id:${data.message._id}`
      : `content:${data.message.sender}|${data.message.content}|${incomingCreatedAt}`;

    if (recentIncomingMessageKeysRef.current.has(dedupeKey)) {
      return;
    }
    recentIncomingMessageKeysRef.current.add(dedupeKey);
    setTimeout(() => {
      recentIncomingMessageKeysRef.current.delete(dedupeKey);
    }, 5000);

    setMessages(prev => {
      const isDuplicate = prev.some(msg => {
        if (msg._id && data.message._id) {
          return msg._id.toString() === data.message._id.toString();
        }

        const msgCreatedAt = msg.createdAt instanceof Date
          ? msg.createdAt.getTime()
          : new Date(msg.createdAt).getTime();

        const timeDiff = Math.abs(msgCreatedAt - incomingCreatedAt);
        return msg.content === data.message.content &&
          msg.sender === data.message.sender &&
          timeDiff < 5000;
      });

      if (isDuplicate) {
        return prev;
      }

      return [...prev, data.message];
    });

    notifyIncomingMessage({
      isBroadcast: !!data.message.isBroadcast,
      content: data.message.type === 'text' ? data.message.content : ''
    });

    if (showTextMessagingModalRef.current) {
      setTimeout(scrollToBottom, 100);
    } else {
      // Advance the polling baseline too, so the next status poll does not
      // re-notify for this same message.
      const next = lastSeenUnreadRef.current + 1;
      lastSeenUnreadRef.current = next;
      setUnreadCount(next);
    }
  };
  handleNewMessageFromRecruiterRef.current = handleNewMessageFromRecruiter;

  const clearQueueStorage = () => {
    try {
      localStorage.removeItem(`queuePos_${boothId}`);
      localStorage.removeItem(`serving_${boothId}`);
      localStorage.removeItem(`queueToken_${boothId}`);
    } catch (_e) { /* ignore */ }
  };

  const leaveCurrentQueue = async ({ leaveAll = false } = {}) => {
    hasLeftQueueRef.current = true;
    if (socket) {
      socket.emit('leave-booth-queue', { boothId, userId: user?._id ? String(user._id) : undefined });
    }
    clearQueueStorage();
    await boothQueueAPI.leaveQueue(boothId, { leaveAll });
  };

  const handleLeaveQueue = async () => {
    if (window.confirm('Are you sure you want to leave the queue?')) {
      try {
        await leaveCurrentQueue();
        showSuccess('Successfully left the queue');
        navigate(`/events/registered/${eventSlug}`);
      } catch (error) {
        hasLeftQueueRef.current = false;
        console.error('Error leaving queue:', error);
        showError('Failed to leave queue');
      }
    }
  };

  const handleReturnToEvent = async () => {
    try {
      await leaveCurrentQueue();
      showSuccess('You left the queue and returned to the event');
    } catch (error) {
      console.error('Error leaving queue while returning to event:', error);
      showError('Could not leave queue cleanly. Returning to event.');
    } finally {
      navigate(`/events/registered/${eventSlug}`);
    }
  };

  const handleExitEvent = async () => {
    if (window.confirm('Are you sure you want to exit the event? This will remove you from the queue.')) {
      try {
        await leaveCurrentQueue({ leaveAll: true });
      } catch (error) {
        console.error('Error leaving queue:', error);
      }
      navigate('/dashboard');
    }
  };

  const handleDeviceSelection = async () => {
    try {
      // Request permissions and enumerate devices
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audios = devices.filter(d => d.kind === 'audioinput');
      const videos = devices.filter(d => d.kind === 'videoinput');

      setAudioInputs(audios);
      setVideoInputs(videos);

      // Load saved preferences
      const savedAudio = localStorage.getItem('preferredAudioDeviceId');
      const savedVideo = localStorage.getItem('preferredVideoDeviceId');
      setSelectedAudioId(savedAudio || audios[0]?.deviceId || '');
      setSelectedVideoId(savedVideo || videos[0]?.deviceId || '');

      setShowDeviceModal(true);
    } catch (error) {
      console.error('Error accessing devices:', error);
      alert('Unable to access camera and microphone. Please check your permissions.');
    }
  };


  const handleDeviceSave = () => {
    if (selectedAudioId) {
      localStorage.setItem('preferredAudioDeviceId', selectedAudioId);
    }
    if (selectedVideoId) {
      localStorage.setItem('preferredVideoDeviceId', selectedVideoId);
    }

    setShowDeviceModal(false);
    showSuccess('Camera and microphone preferences saved successfully!');
  };

  const handleDeviceCancel = () => {
    setShowDeviceModal(false);
  };

  const handleMobilePanelClose = () => {
    setMobilePanelOpen(false);
    announceToScreenReader('Queue panel closed');
  };

  const handleMobilePanelToggle = () => {
    const newState = !mobilePanelOpen;
    setMobilePanelOpen(newState);
    announceToScreenReader(newState ? 'Queue panel opened' : 'Queue panel closed');
  };

  // Focus management for modals
  const openModal = () => {
    previousFocusRef.current = document.activeElement;
    setShowMessageModal(true);
  };

  const closeModal = () => {
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    stopRecordingStream();
    setShowMessageModal(false);
    setRecordingError('');
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  };

  // Focus trap for modal
  const handleModalKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeModal();
      return;
    }

    if (event.key === 'Tab') {
      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    }
  };

  // Focus first element when modal opens
  useEffect(() => {
    if (showMessageModal && modalRef.current) {
      const firstFocusable = modalRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  }, [showMessageModal]);

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
    if (recordingVideoRef.current) {
      recordingVideoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (isRecording && messageType === 'video' && recordingStreamRef.current && recordingVideoRef.current) {
      recordingVideoRef.current.srcObject = recordingStreamRef.current;
    }
  }, [isRecording, messageType]);

  // Close + reset for the "Leave a Message" dialog.
  const closeLeaveMessageModal = () => {
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    stopRecordingStream();
    setShowLeaveMessageModal(false);
    setRecordedBlob(null);
    setMessageContent('');
    setShowMessagePreview(false);
    setRecordingError('');
  };

  // A recording (and its blob: preview URL) belongs to the type it was made
  // for; drop it on a type change so the text box never shows a blob: URL and
  // a video is never uploaded as audio.
  const handleLeaveMessageTypeChange = (type) => {
    if (recordedBlob || messageContent.startsWith('blob:')) {
      handleRetakeRecording();
    }
    setRecordingError('');
    setMessageType(type);
  };

  // Stopping a recording swaps the record button for the preview, and Retake
  // swaps it back. Both remove the focused button, so move focus to the new
  // control instead of letting it fall to <body> (WCAG 2.4.3).
  useEffect(() => {
    if (!showLeaveMessageModal) return;
    const target = leaveFocusTargetRef.current;
    leaveFocusTargetRef.current = null;
    if (target === 'preview' && leavePreviewRef.current) {
      leavePreviewRef.current.focus();
    } else if (target === 'record' && leaveRecordBtnRef.current) {
      leaveRecordBtnRef.current.focus();
    }
  }, [recordedBlob, showLeaveMessageModal]);

  // Focus management (move-in, trap, restore + Escape) for the dialogs that
  // were missing it, so keyboard and screen reader users land on the dialog.
  const leaveMessageDialog = useDialogFocus(showLeaveMessageModal, closeLeaveMessageModal);
  const textMessagingDialog = useDialogFocus(
    showTextMessagingModal,
    () => setShowTextMessagingModal(false)
  );

  // Move focus into the queue options dialog when it opens on mobile,
  // and restore focus to the triggering control when it closes.
  useEffect(() => {
    if (!isMobile) return;

    if (mobilePanelOpen) {
      queuePanelPreviousFocusRef.current = document.activeElement;
      if (queuePanelRef.current) {
        queuePanelRef.current.focus();
      }
    } else if (queuePanelPreviousFocusRef.current) {
      queuePanelPreviousFocusRef.current.focus();
      queuePanelPreviousFocusRef.current = null;
    }
  }, [mobilePanelOpen, isMobile]);

  // Keyboard event handler for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && mobilePanelOpen) {
      handleMobilePanelClose();
    }
  };

  // Add keyboard event listener and window resize listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    const handleResize = () => {
      const mobile = window.innerWidth <= 1199;
      setIsMobile(mobile);
      if (!mobile && mobilePanelOpen) {
        setMobilePanelOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [mobilePanelOpen]);

  const handleSendTextMessage = async () => {
    if (boothClosed) {
      setShowTextMessagingModal(false);
      showError(BOOTH_CLOSED_BANNER_MESSAGE);
      return;
    }
    try {
      if (!textMessageContent.trim()) return;

      setIsUploading(true);
      const messageData = {
        boothId,
        type: 'text',
        content: textMessageContent,
        queueToken
      };

      await boothQueueAPI.sendMessage(messageData);

      // Add message to local state immediately
      const newMessage = {
        type: 'text',
        content: textMessageContent,
        sender: 'jobseeker',
        createdAt: new Date(),
        isRead: true
      };
      setMessages(prev => [...prev, newMessage]);
      // Announce the sent message once for screen reader users.
      announceToScreenReader(`You said: ${newMessage.content}`);
      setTextMessageContent('');
      // Removed toast notification for message sent

      // Scroll to bottom
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLeaveWithMessage = async () => {
    if (boothClosed) {
      setShowLeaveMessageModal(false);
      showError(BOOTH_CLOSED_BANNER_MESSAGE);
      return;
    }
    try {
      let finalContent = messageContent;

      if (messageType === 'text') {
        if (!messageContent.trim()) {
          showError('Please enter a message');
          return;
        }
        finalContent = messageContent.trim();
      } else {
        if (!recordedBlob) {
          showError('Please record a message first');
          return;
        }

        setIsUploading(true);
        const file = new File(
          [recordedBlob],
          `leave_message_${Date.now()}.${messageType === 'audio' ? 'webm' : 'webm'}`,
          { type: recordedBlob.type }
        );

        const uploadResult = messageType === 'audio'
          ? await uploadAudioToS3(file, undefined, { purpose: 'queue-message' })
          : await uploadVideoToS3(file, undefined, { purpose: 'queue-message' });

        finalContent = uploadResult.downloadUrl;
      }

      // Stop status polling before leaving so in-flight requests don't 404
      hasLeftQueueRef.current = true;
      setIsUploading(true);
      await boothQueueAPI.leaveWithMessage({
        boothId,
        type: messageType,
        content: finalContent,
        queueToken
      });

      setShowLeaveMessageModal(false);
      setRecordedBlob(null);
      setMessageContent('');
      try {
        localStorage.removeItem(`queueToken_${boothId}`);
        localStorage.removeItem(`queuePos_${boothId}`);
        localStorage.removeItem(`serving_${boothId}`);
      } catch (_e) { /* ignore */ }
      showSuccess('Message sent! You have left the queue.');
      navigate(`/events/registered/${eventSlug}`);
    } catch (error) {
      // Allow polling again if the leave request failed
      hasLeftQueueRef.current = false;
      console.error('Error leaving with message:', error);
      showError(error.response?.data?.message || 'Failed to send leave message');
    } finally {
      setIsUploading(false);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const loadMessages = async () => {
    if (!queueEntryId) {
      console.error('No queue entry ID available');
      return;
    }
    try {
      const response = await boothQueueAPI.getMessages(queueEntryId);
      if (response.success) {
        const apiMessages = response.messages || [];
        
        // Merge with existing messages to avoid losing any that arrived via socket
        setMessages(prev => {
          const mergedMessages = [...apiMessages];
          const existingIds = new Set(apiMessages.map(m => m._id || `${m.content}-${m.createdAt}-${m.sender}`));
          
          prev.forEach(msg => {
            const msgId = msg._id || `${msg.content}-${msg.createdAt}-${msg.sender}`;
            if (!existingIds.has(msgId)) {
              mergedMessages.push(msg);
            }
          });
          
          // Sort by timestamp to ensure correct order
          return mergedMessages.sort((a, b) => {
            const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
            const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
            return timeA - timeB;
          });
        });
        
        setUnreadCount(0); // Mark as read when viewing
        lastSeenUnreadRef.current = 0;
        // Scroll to bottom after messages load
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleViewMessages = async () => {
    if (boothClosed) {
      showError(BOOTH_CLOSED_BANNER_MESSAGE);
      return;
    }
    setShowTextMessagingModal(true);
    await loadMessages();
  };

  const handleSendMessage = async () => {
    if (boothClosed) {
      setShowMessageModal(false);
      showError(BOOTH_CLOSED_BANNER_MESSAGE);
      return;
    }
    try {
      setIsUploading(true);
      let finalContent = messageContent;

      // If it's audio/video, upload to S3 first
      if ((messageType === 'audio' || messageType === 'video') && recordedBlob) {
        const fileName = `message_${Date.now()}.${messageType === 'audio' ? 'webm' : 'webm'}`;
        const file = new File([recordedBlob], fileName, {
          type: messageType === 'audio' ? 'audio/webm' : 'video/webm'
        });

        const uploadResult = messageType === 'audio'
          ? await uploadAudioToS3(file, undefined, { purpose: 'queue-message' })
          : await uploadVideoToS3(file, undefined, { purpose: 'queue-message' });

        finalContent = uploadResult.downloadUrl;
      }

      const messageData = {
        boothId,
        type: messageType,
        content: finalContent,
        queueToken
      };

      await boothQueueAPI.sendMessage(messageData);

      // Reset all message-related state
      setShowMessageModal(false);
      setShowMessagePreview(false);
      setMessageContent('');
      setRecordedBlob(null);
      setMessageType('text');

      showSuccess('Message sent successfully!');

      // If audio/video message, leave queue and navigate to event detail
      if (messageType === 'audio' || messageType === 'video') {
        try {
          await leaveCurrentQueue();
        } catch (error) {
          console.error('Error leaving queue after message:', error);
        }
        navigate(`/events/registered/${eventSlug}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message');
    } finally {
      setIsUploading(false);
    }
  };

  const startRecording = async () => {
    setRecordingError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: messageType === 'video'
      });

      recordingStreamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      recordedChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: messageType === 'video' ? 'video/webm' : 'audio/webm'
        });

        setRecordedBlob(blob);

        // Create preview URL for audio/video
        const previewUrl = URL.createObjectURL(blob);
        setMessageContent(previewUrl);
        setShowMessagePreview(true);

        stopRecordingStream();
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      stopRecordingStream();
      // Shown inline in the dialog and kept until the next attempt, instead of
      // a blocking alert() or a toast that disappears (WCAG 2.2.1, 3.3.1).
      const device = messageType === 'video' ? 'camera and microphone' : 'microphone';
      if (error?.name === 'NotAllowedError') {
        setRecordingError(`Access to your ${device} was blocked. Allow it in your browser settings, then try again.`);
      } else if (error?.name === 'NotFoundError') {
        setRecordingError(`No ${device} was found. Connect one, then try again.`);
      } else {
        setRecordingError(`Could not start recording. Check that your ${device} is not in use by another app, then try again.`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleMessagePreview = () => {
    if (recordedBlob) {
      setShowMessagePreview(true);
    }
  };

  const handleRetakeRecording = () => {
    if (messageContent.startsWith('blob:')) {
      URL.revokeObjectURL(messageContent);
    }
    stopRecordingStream();
    setRecordedBlob(null);
    setMessageContent('');
    setShowMessagePreview(false);
  };

  const renderLiveRecordingPreview = () => (
    isRecording && messageType === 'video' ? (
      <div className="media-preview recording-live-preview">
        <video
          ref={recordingVideoRef}
          autoPlay
          playsInline
          muted
          aria-label="Live camera preview while recording"
          className="recording-live-video"
        />
      </div>
    ) : null
  );

  if (loading) {
    return (
      <div className="booth-queue-waiting">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading queue information...</p>
        </div>
      </div>
    );
  }

  const isEmployerPageMode = booth?.waitingAreaMode === 'employerPage';
  const isMobilePanelHidden = isMobile && !mobilePanelOpen;
  // While the full-screen video call overlay is active, the waiting-area page
  // (global header logout button, queue-options sidebar, etc.) is still in the
  // DOM behind it. Hide that content from keyboard focus and assistive tech so
  // users can only reach the visible in-call controls.
  const isBehindCallOverlay = isInCall && !!callInvitation;
  const peopleAheadText = peopleAhead === 0
    ? 'You are next in the queue.'
    : `There ${peopleAhead === 1 ? 'is' : 'are'} ${peopleAhead} ${peopleAhead === 1 ? 'person' : 'people'} ahead of you in the queue.`;

  return (
    <div className={`booth-queue-waiting${isEmployerPageMode ? ' employer-mode' : ''}`}>
      {/* `display: contents` keeps the flex layout intact while letting us mark
          the entire waiting-area subtree inert during an active call. */}
      <div
        style={{ display: 'contents' }}
        inert={isBehindCallOverlay ? '' : undefined}
        aria-hidden={isBehindCallOverlay ? 'true' : undefined}
      >
        {/* Skip link must be first focusable element in DOM order */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>

        {/* Global header with event branding */}
        <AdminHeader brandingLogo={event?.logoUrl || event?.logo || ''} brandingLogoAlt={event?.logoAltText || ''} hideMenuToggle={true} />

        <Helmet>
          <title>{`${booth?.name || 'Company'} Waiting Area - abilityconnect`}</title>
        </Helmet>

      {/* Main content area */}
      <div className="waiting-layout">
        {/* Mobile backdrop overlay */}
        {mobilePanelOpen && (
          <div
            className="mobile-backdrop"
            onClick={handleMobilePanelClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleMobilePanelClose();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close queue panel"
          />
        )}

        {/* Left sidebar with queue info and actions (moved from right) */}
        <div
          id="queue-panel"
          ref={queuePanelRef}
          className={`waiting-sidebar-left mobile-collapsible ${mobilePanelOpen ? 'open' : ''}`}
          role={isMobile && mobilePanelOpen ? 'dialog' : 'complementary'}
          aria-modal={isMobile && mobilePanelOpen ? 'true' : undefined}
          aria-label="Queue options and actions"
          aria-hidden={isMobilePanelHidden ? 'true' : undefined}
          inert={isMobilePanelHidden ? '' : undefined}
          tabIndex={isMobile && mobilePanelOpen ? -1 : undefined}
        >
          <div className="mobile-panel-close-row">
            <button
              className="mobile-panel-close-btn"
              onClick={handleMobilePanelClose}
              aria-label="Close queue options panel"
              type="button"
            >
              <FaTimes aria-hidden="true" />
              <span className="sr-only">Close queue options panel</span>
            </button>
          </div>

          {/* Desktop: Show event info and queue status */}
          <div className="sidebar-header sidebar-header-desktop">
            <div className="event-info">
              <h1 className="event-title" id="event-title">{event?.name || 'ABILITY Job Fair - Event'}</h1>
              <h2 className="booth-subtitle" id="booth-title">{booth?.name || 'Company Booth'}</h2>
              {isTestQueue && (
                <p className="waiting-test-queue-note">
                  <TestQueueTag /> You joined with the booth's test link.
                </p>
              )}
            </div>
          </div>

          {/* Mockup order: status line, then the Your Place in the Queue card. */}
          <div className={`queue-status queue-status-desktop${boothClosed ? ' booth-closed-status' : ''}`}>
            <span className={`status-dot ${boothClosed ? 'closed' : 'waiting'}`} aria-hidden="true"></span>
            <span className="status-text">{boothClosed ? 'Booth closed' : 'Waiting in the queue'}</span>
          </div>

          {!boothClosed && (
            <div className="queue-numbers queue-numbers-desktop">
              <div className="queue-number-card">
                {/* The position number is not shown. The h2 puts the card in the heading
                    outline, and the visible helper text is what screen readers read, so
                    sighted and screen-reader users get the same information. */}
                <h2 className="queue-label" id="total-waiting-label">Your Place in the Queue</h2>
                <p className="queue-helper-text">{peopleAheadText}</p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="sidebar-actions">
            {!boothClosed && (
              <>
                <button
                  className="sidebar-action-btn camera-btn"
                  onClick={handleDeviceSelection}
                  aria-label="Select camera and microphone devices"
                  type="button"
                >
                  <FaVideo aria-hidden="true" /> Select camera & mic
                </button>

                <button
                  className="sidebar-action-btn message-btn"
                  onClick={handleViewMessages}
                  aria-label="View and send text messages to the recruiter"
                  type="button"
                >
                  <FaCommentDots aria-hidden="true" />
                  Send Messages
                  {unreadCount > 0 && (
                    <span className="unread-badge" aria-label={`${unreadCount} unread messages`}>
                      {unreadCount}
                    </span>
                  )}
                </button>

                <button
                  className="sidebar-action-btn leave-message-btn"
                  onClick={() => { setShowLeaveMessageModal(true); setMessageType('video'); }}
                  aria-label="Leave a message and exit the queue"
                  type="button"
                >
                  <FaCommentDots aria-hidden="true" /> Leave Message
                </button>
              </>
            )}

            {!boothClosed && (
              <button
                className="sidebar-action-btn refresh-btn"
                onClick={() => window.location.reload()}
                aria-label="Refresh connection to update queue status"
                type="button"
              >
                <FaSyncAlt aria-hidden="true" /> Refresh connection
              </button>
            )}

            <button
              className="sidebar-action-btn return-btn-alt"
              onClick={handleReturnToEvent}
              aria-label="Return to main event page and leave queue"
              type="button"
            >
              <FaArrowLeft aria-hidden="true" /> Return to main event
            </button>

            <button
              className="sidebar-action-btn exit-btn"
              onClick={handleExitEvent}
              aria-label="Exit the event and leave the queue"
              type="button"
            >
              <FaSignOutAlt aria-hidden="true" /> Exit the event
            </button>
          </div>
        </div>

        {/* Main content */}
        <main id="main-content" className={`waiting-main${isEmployerPageMode ? ' employer-mode' : ''}`} tabIndex={-1} aria-label="Queue waiting area - main content">
          {/* Mobile toggle for queue panel - only shows action buttons */}
          <button
            className="mobile-toggle-btn"
            onClick={handleMobilePanelToggle}
            aria-expanded={mobilePanelOpen}
            aria-controls="queue-panel"
            aria-label={
              mobilePanelOpen
                ? 'Close queue options panel'
                : `Open queue options panel${unreadCount > 0 && !boothClosed
                  ? `, ${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`
                  : ''}`
            }
            type="button"
          >
            {mobilePanelOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
            {mobilePanelOpen ? 'Close Queue Options' : 'Queue Options'}
            {/* The Send Messages badge sits inside the collapsed panel, so mirror the
                count here where it stays visible on small screens. */}
            {!mobilePanelOpen && unreadCount > 0 && !boothClosed && (
              <span className="unread-badge" aria-hidden="true">{unreadCount}</span>
            )}
          </button>

          {/* Combined booth logo and queue card for mobile - always visible on mobile.
              No wrapping role/aria-label here: a labeled region around all of this
              caused iOS VoiceOver to treat the whole block as one stop and skip the
              event name, booth name, waiting status, and queue numbers. */}
          <div className="mobile-queue-header">
            {/* Row 1: logo + identity */}
            <div className="mobile-identity-row">
              <div className="mobile-booth-logo" aria-hidden="true">
                {booth?.logoUrl ? (
                  <img
                    src={booth.logoUrl}
                    alt=""
                    className="booth-logo-modern"
                  />
                ) : (
                  <div className="booth-logo-modern-placeholder" role="img" aria-label={`${booth?.name || 'Company'} logo placeholder`}>
                    <span aria-hidden="true">{booth?.name?.[0] || 'C'}</span>
                  </div>
                )}
              </div>
              <div className="mobile-event-info-compact">
                {/* Event name as the page-level heading and booth name as a
                    sub-heading so screen reader users can navigate to and hear
                    both via the headings rotor. */}
                <h1 className="mobile-event-title" id="event-title-mobile">{event?.name || 'ABILITY Job Fair - Event'}</h1>
                <h2 className="mobile-booth-subtitle" id="booth-title-mobile">{booth?.name || 'Company Booth'}</h2>
                {isTestQueue && (
                  <p className="waiting-test-queue-note">
                    <TestQueueTag /> You joined with the booth's test link.
                  </p>
                )}
                {boothClosed && (
                  <div className="queue-status queue-status-mobile booth-closed-status">
                    <span className="status-dot closed" aria-hidden="true"></span>
                    <span className="status-text">Booth closed</span>
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Queue status card — h2 exposes the section in the heading
                outline; the visible helper text is read during linear/swipe
                navigation. The position number is not shown. */}
            {!boothClosed && (
              <div className="queue-numbers queue-numbers-mobile">
                <div className="queue-number-card queue-number-card-mobile">
                  {/* Always-visible status line on small screens (Sprint 7: "Waiting in
                      the queue" must show on mobile). */}
                  <p className="mqc-status-line">
                    <span className="status-dot waiting" aria-hidden="true"></span>
                    Waiting in the queue
                  </p>
                  <div className="mqc-label-group">
                    <h2 className="queue-label" id="total-waiting-label-mobile">Your Place in the Queue</h2>
                  </div>
                  <p className="queue-helper-text queue-helper-text-mobile">{peopleAheadText}</p>
                </div>
              </div>
            )}
          </div>

          <BoothAvailabilityInfo booth={booth} event={event} />

          {boothClosed && (
            <div
              className="recruiter-end-banner booth-closed-banner"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <p className="recruiter-end-banner-text">{BOOTH_CLOSED_BANNER_MESSAGE}</p>
            </div>
          )}

          {/* Waiting message on top of placeholders */}
          {showRecruiterEndBanner && (
            <div
              className="recruiter-end-banner"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <p className="recruiter-end-banner-text">{recruiterUnavailableMessage}</p>
              <button
                type="button"
                className="recruiter-end-banner-cta"
                onClick={() => { setShowLeaveMessageModal(true); setMessageType('video'); }}
                aria-label="Leave a message for recruiters"
              >
                Leave a message
              </button>
            </div>
          )}

          {!isEmployerPageMode && !boothClosed && (
            <div className="waiting-message-header waiting-message-centered" role="status" aria-live="polite">
              <h3>You are now in the queue.</h3>
              <p>An invitation to join will appear when it's your turn.</p>
            </div>
          )}

          {/* ARIA Live Regions for Screen Reader Announcements */}
          <div aria-live="assertive" aria-atomic="true" className="sr-only">
            {announcements.map(announcement => (
              <div key={announcement.id}>{announcement.message}</div>
            ))}
          </div>

          {isEmployerPageMode ? (
            <div className="waiting-employer-pane">
              <EmployerPageTemplate booth={booth} />
            </div>
          ) : (
            <div className="content-grid-expanded">
              {(booth?.richSections && booth.richSections.length > 0
                ? booth.richSections
                  .filter(s => s.isActive !== false)
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .slice(0, 3)
                : [
                  {
                    contentHtml: '<div style="width:100%;height:180px;border-radius:8px;background:linear-gradient(135deg,#f3f4f6,#e5e7eb);display:flex;align-items:center;justify-content:center;color:#6b7280;font-weight:600;">Welcome to the booth</div>'
                  },
                  {
                    contentHtml: '<div style="width:100%;height:180px;border-radius:8px;background:linear-gradient(135deg,#f3f4f6,#e5e7eb);display:flex;align-items:center;justify-content:center;color:#6b7280;font-weight:600;">Resources coming soon</div>'
                  },
                  {
                    contentHtml: '<div style="width:100%;height:180px;border-radius:8px;background:linear-gradient(135deg,#f3f4f6,#e5e7eb);display:flex;align-items:center;justify-content:center;color:#6b7280;font-weight:600;">Please stay on this page</div>'
                  }
                ]
              ).map((section, idx) => (
                <div key={section._id || idx} className="content-card-expanded">
                  {section.contentHtml ? (
                    <div className="content-body" dangerouslySetInnerHTML={{ __html: hydrateStreamMediaUrls(section.contentHtml) }} />
                  ) : (
                    <p className="content-placeholder">Content will be available soon.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Removed old right sidebar */}
      </div>
      </div>


      {/* Message Modal */}
      {showMessageModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onKeyDown={handleModalKeyDown}
        >
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <h3 id="modal-title">Send Message to Recruiter</h3>
              <button
                className="modal-close"
                onClick={closeModal}
                aria-label="Close message dialog"
                type="button"
              >
                ×
              </button>
            </div>

            <div className="message-form">
              <fieldset className="message-type-selector">
                <legend className="sr-only">Choose message type</legend>
                <label>
                  <input
                    type="radio"
                    name="messageType"
                    value="text"
                    checked={messageType === 'text'}
                    onChange={(e) => setMessageType(e.target.value)}
                    aria-describedby="text-message-desc"
                  />
                  Text Message
                  <span id="text-message-desc" className="sr-only">Send a written text message</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="messageType"
                    value="audio"
                    checked={messageType === 'audio'}
                    onChange={(e) => setMessageType(e.target.value)}
                    aria-describedby="audio-message-desc"
                  />
                  Audio Message
                  <span id="audio-message-desc" className="sr-only">Record and send an audio message</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="messageType"
                    value="video"
                    checked={messageType === 'video'}
                    onChange={(e) => setMessageType(e.target.value)}
                    aria-describedby="video-message-desc"
                  />
                  Video Message
                  <span id="video-message-desc" className="sr-only">Record and send a video message</span>
                </label>
              </fieldset>

              {messageType === 'text' ? (
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Type your message here..."
                  rows={4}
                  aria-label="Message content"
                  aria-describedby="text-input-help"
                />
              ) : (
                <div className="recording-controls">
                  {!recordedBlob ? (
                    <div className="recording-section">
                      {renderLiveRecordingPreview()}
                      {!isRecording ? (
                        <button onClick={startRecording} className="record-btn">
                          Start Recording {messageType === 'video' ? 'Video' : 'Audio'}
                        </button>
                      ) : (
                        <button onClick={stopRecording} className="stop-btn">
                          Stop Recording
                        </button>
                      )}
                      {isRecording && (
                        <p className="recording-status">Recording in progress...</p>
                      )}
                      {recordingError && (
                        <p className="leave-message-error" role="alert">{recordingError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="recording-preview-section">
                      <div className="preview-controls">
                        <button onClick={handleMessagePreview} className="btn-preview">
                          Preview {messageType === 'video' ? 'Video' : 'Audio'}
                        </button>
                        <button onClick={handleRetakeRecording} className="btn-retake">
                          Retake Recording
                        </button>
                      </div>

                      {showMessagePreview && (
                        <div className="media-preview">
                          {messageType === 'video' ? (
                            <video
                              src={messageContent}
                              controls
                              aria-label="Preview of your recorded video message"
                              style={{
                                width: '100%',
                                maxWidth: '300px',
                                borderRadius: '8px',
                                backgroundColor: '#000'
                              }}
                            />
                          ) : (
                            <audio
                              src={messageContent}
                              controls
                              aria-label="Preview of your recorded audio message"
                              style={{ width: '100%' }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={closeModal}
                  className="btn-cancel"
                  type="button"
                  aria-label="Cancel and close dialog"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!messageContent || isUploading || (messageType !== 'text' && !recordedBlob)}
                  className="btn-send"
                  type="button"
                  aria-label={isUploading ? 'Sending message, please wait' : 'Send message to recruiter'}
                >
                  {isUploading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Message Modal */}
      {showLeaveMessageModal && (
        <div className="modal-overlay leave-message-overlay">
          <div
            className="leave-message-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="leave-modal-title"
            aria-describedby="leave-modal-desc"
            ref={leaveMessageDialog.dialogRef}
            tabIndex={-1}
            onKeyDown={leaveMessageDialog.onKeyDown}
          >
            <div className="leave-message-header">
              <h2 id="leave-modal-title" className="leave-message-title">Leave a Message</h2>
              <button
                type="button"
                className="leave-message-close"
                onClick={closeLeaveMessageModal}
                aria-label="Close"
              >
                <FaTimes aria-hidden="true" focusable="false" />
              </button>
            </div>

            <div className="leave-message-body">
              <p id="leave-modal-desc" className="leave-message-intro">
                Leave a video, audio or text message. Sending it will remove you from the queue.
              </p>

              <fieldset
                className="leave-message-types"
                disabled={isRecording || isUploading}
              >
                <legend className="leave-message-label">Message type</legend>
                <div className="leave-message-type-grid">
                  {[
                    { value: 'video', label: 'Video', Icon: FaVideo },
                    { value: 'audio', label: 'Audio', Icon: FaMicrophone },
                    { value: 'text', label: 'Text', Icon: FaCommentDots }
                  ].map(({ value, label, Icon }) => (
                    <label
                      key={value}
                      className={`leave-message-type${messageType === value ? ' is-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="leaveMessageType"
                        value={value}
                        checked={messageType === value}
                        onChange={() => handleLeaveMessageTypeChange(value)}
                      />
                      <Icon className="leave-message-type-icon" aria-hidden="true" focusable="false" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                {isRecording && (
                  <p className="leave-message-hint">Stop recording to change the message type.</p>
                )}
              </fieldset>

              {messageType === 'text' ? (
                <div className="leave-message-field">
                  <label htmlFor="leave-message-text" className="leave-message-label">
                    Your message
                  </label>
                  <textarea
                    id="leave-message-text"
                    className="leave-message-textarea"
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    rows={5}
                    disabled={isUploading}
                  />
                </div>
              ) : (
                <div className="leave-message-recorder">
                  {!recordedBlob ? (
                    <>
                      {renderLiveRecordingPreview()}
                      {!isRecording && (
                        <p className="leave-message-recorder-hint">
                          {messageType === 'video'
                            ? 'Your camera and microphone turn on when you start recording.'
                            : 'Your microphone turns on when you start recording.'}
                        </p>
                      )}
                      {/* One button that toggles, so focus stays put when recording starts. */}
                      <button
                        ref={leaveRecordBtnRef}
                        type="button"
                        className={`leave-dialog-btn ${isRecording ? 'leave-dialog-btn--stop' : 'leave-dialog-btn--record'}`}
                        onClick={isRecording
                          ? () => { leaveFocusTargetRef.current = 'preview'; stopRecording(); }
                          : startRecording}
                      >
                        {isRecording
                          ? <FaStop aria-hidden="true" focusable="false" />
                          : <span className="leave-message-record-dot" aria-hidden="true" />}
                        {isRecording
                          ? 'Stop Recording'
                          : `Start Recording ${messageType === 'video' ? 'Video' : 'Audio'}`}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="leave-message-preview">
                        {messageType === 'video' ? (
                          <video
                            ref={leavePreviewRef}
                            src={messageContent}
                            controls
                            aria-label="Preview of your recorded video message"
                          />
                        ) : (
                          <audio
                            ref={leavePreviewRef}
                            src={messageContent}
                            controls
                            aria-label="Preview of your recorded audio message"
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        className="leave-dialog-btn leave-dialog-btn--secondary"
                        onClick={() => { leaveFocusTargetRef.current = 'record'; handleRetakeRecording(); }}
                        disabled={isUploading}
                      >
                        <FaSyncAlt aria-hidden="true" focusable="false" />
                        Record Again
                      </button>
                    </>
                  )}

                  {/* Always mounted so screen readers announce each change. */}
                  <p className="leave-message-status" role="status">
                    {isRecording && (
                      <>
                        <span className="leave-message-live-dot" aria-hidden="true" />
                        Recording…
                      </>
                    )}
                    {!isRecording && recordedBlob &&
                      `Your ${messageType} message is ready. Play it back to check it, then send it.`}
                  </p>

                  {recordingError && (
                    <p className="leave-message-error" role="alert">{recordingError}</p>
                  )}
                </div>
              )}
            </div>

            <div className="leave-message-footer">
              {!isUploading && !(messageType === 'text' ? messageContent.trim() : recordedBlob) && (
                <p id="leave-send-hint" className="leave-message-hint">
                  {messageType === 'text'
                    ? 'Type a message to enable sending.'
                    : isRecording
                      ? 'Stop recording to review your message before sending.'
                      : 'Record a message to enable sending.'}
                </p>
              )}
              <div className="leave-message-actions">
                <button
                  type="button"
                  className="leave-dialog-btn leave-dialog-btn--secondary"
                  onClick={closeLeaveMessageModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="leave-dialog-btn leave-dialog-btn--primary"
                  onClick={handleLeaveWithMessage}
                  disabled={messageType === 'text' ? (!messageContent.trim() || isUploading) : (!recordedBlob || isUploading)}
                >
                  {isUploading ? 'Sending…' : 'Send & Leave Queue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text Messaging Modal */}
      {showTextMessagingModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="text-modal-title"
          ref={textMessagingDialog.dialogRef}
          tabIndex={-1}
          onKeyDown={textMessagingDialog.onKeyDown}
        >
          <div className="modal-content text-messaging-modal">
            <div className="modal-header">
              <h3 id="text-modal-title">Messages</h3>
              <button
                className="modal-close"
                onClick={() => setShowTextMessagingModal(false)}
                aria-label="Close messages dialog"
                type="button"
              >
                ×
              </button>
            </div>

            <div className="messages-container">
              <p className="messages-instruction">
                Type your message to the recruiter(s) in this booth. Please
                understand Recruiters may be in meetings.
              </p>
              <div
                className="messages-list"
                role="log"
                aria-label="Messages"
              >
                {messages.length === 0 ? (
                  <p className="no-messages">No messages yet</p>
                ) : (
                  <>
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`message-bubble ${msg.sender === 'jobseeker' ? 'sent' : 'received'}`}
                      >
                        <div className="message-sender">
                          {msg.sender === 'jobseeker' ? 'You' : 'Recruiter'}
                        </div>
                        <div className="message-text">{msg.content}</div>
                        <div className="message-time">
                          {new Date(msg.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="message-input-area">
                <textarea
                  value={textMessageContent}
                  onChange={(e) => setTextMessageContent(e.target.value)}
                  placeholder="Type your message..."
                  rows={3}
                  aria-label="Message content"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendTextMessage();
                    }
                  }}
                />
                <button
                  onClick={handleSendTextMessage}
                  disabled={!textMessageContent.trim() || isUploading}
                  className="btn-send-message"
                  type="button"
                >
                  {isUploading ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Call Component */}
      {isInCall && callInvitation && (
        <VideoCall
          callData={callInvitation}
          onCallEnd={handleCallEnd}
        />
      )}

      {/* Call Invitation Modal */}
      {showInviteModal && (
        <CallInviteModal
          recruiterName={pendingInvitation?.recruiter?.name}
          boothName={booth?.name}
          eventName={event?.name}
          audioInputs={audioInputs}
          videoInputs={videoInputs}
          selectedAudioId={selectedAudioId}
          selectedVideoId={selectedVideoId}
          onChangeAudio={setSelectedAudioId}
          onChangeVideo={setSelectedVideoId}
          onAccept={handleAcceptInvite}
          onDecline={handleDeclineInvite}
        />
      )}

      {/* Device Test Modal */}
      <DeviceTestModal
        isOpen={showDeviceModal}
        onClose={handleDeviceCancel}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        selectedAudioId={selectedAudioId}
        selectedVideoId={selectedVideoId}
        onChangeAudio={setSelectedAudioId}
        onChangeVideo={setSelectedVideoId}
        onSave={handleDeviceSave}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
